// PromoGrantedDialog — анонс-попап «Вам открыт Pro на 12 месяцев».
//
// Показывается один раз каждому пользователю, которому выдан промо-Pro
// (auth.me.promoPlanGrantedAt != null && promoPlanSeenAt == null). На закрытие
// вызывает api.users.acknowledgePromo (идемпотентно проставляет promo_plan_seen_at)
// и перечитывает auth.me, чтобы попап больше не открывался.
//
// В приложении из App Store не показывается вовсе (решение владельца
// 17.09.2026, guideline 3.1.1): тариф там не называется нигде — человек просто
// пользуется приложением. Отметку «увидел» при этом не ставим: откроет веб —
// увидит анонс там, где тарифная система видна.
//
// Смонтирован глобально в __root.tsx (см. rules/frontend-state.md §5.4 —
// глобальные overlay-диалоги монтируются в layout).

import { Button } from "@studyhub/ui/components/button";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@studyhub/ui/components/responsive-dialog";
import {
	IconBolt,
	IconCrown,
	IconHeadphones,
	IconInfinity,
	IconSparkles,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { api } from "@/lib/trpc-client";
import { useAuth } from "@/providers/auth-provider";

const PERKS = [
	{ key: "unlimited", icon: IconInfinity },
	{ key: "ai", icon: IconSparkles },
	{ key: "recordings", icon: IconHeadphones },
	{ key: "priority", icon: IconBolt },
] as const;

export function PromoGrantedDialog(): React.ReactElement | null {
	const { t } = useTranslation("billing");
	const { showPromoDialog, refresh } = useAuth();
	const acknowledge = api.users.acknowledgePromo.useMutation();
	const commerceVisible = useCommerceVisible();

	if (!showPromoDialog || !commerceVisible) return null;

	const handleClose = (): void => {
		// Идемпотентный серверный UPDATE → затем перечитываем auth.me, чтобы
		// showPromoDialog стал false и попап не открылся повторно.
		acknowledge.mutate(undefined, {
			onSettled: () => {
				void refresh();
			},
		});
	};

	return (
		<ResponsiveDialog
			open
			onOpenChange={(open) => {
				if (!open) handleClose();
			}}
		>
			<ResponsiveDialogContent
				className="sm:max-w-md"
				drawerClassName="pb-[max(1.5rem,env(safe-area-inset-bottom,0px))]"
			>
				<ResponsiveDialogHeader className="items-center text-center">
					<div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-linear-to-br from-warning/20 to-primary/20">
						<IconCrown size={24} className="text-warning" />
					</div>
					<ResponsiveDialogTitle className="text-xl">{t("promo.title")}</ResponsiveDialogTitle>
					<ResponsiveDialogDescription className="text-center">
						{t("promo.subtitle")}
					</ResponsiveDialogDescription>
				</ResponsiveDialogHeader>

				<ul className="flex flex-col gap-2.5 py-2">
					{PERKS.map(({ key, icon: Icon }) => (
						<li key={key} className="flex items-center gap-2.5 text-sm text-foreground">
							<span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
								<Icon size={14} stroke={1.75} />
							</span>
							<span>{t(`promo.perks.${key}`)}</span>
						</li>
					))}
				</ul>

				<p className="text-center text-xs text-muted-foreground">{t("promo.footnote")}</p>

				<Button
					onClick={handleClose}
					disabled={acknowledge.isPending}
					className="mt-1 w-full cursor-pointer disabled:cursor-not-allowed pointer-coarse:h-11"
				>
					{t("promo.cta")}
				</Button>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}
